import { useState, useEffect, useRef } from "react";
import { X, CaretRight, CaretDown, MagnifyingGlass, Check, Clock, ArrowClockwise, Trash, Info } from "@phosphor-icons/react";
import { T } from "./themes";
import _ComicCard from "./components/ComicCard";
import _Btn from "./components/Btn";
import _Field from "./components/Field";
import { TODAY_ISO, fmtDate, fmtDuration } from "./constants";
import { isNative } from "./llm";

// V28 FIX 3: Muscle group minimum weekly sets thresholds
const VOLUME_THRESHOLDS = {
  Chest: 10, Back: 10, Lats: 10, Shoulders: 8, Quads: 12, Hamstrings: 12, Glutes: 12,
  Biceps: 6, Triceps: 6, Core: 6, Abs: 6, Calves: 6, Forearms: 6, Traps: 6,
  RearDelts: 8, FrontDelts: 8, Obliques: 6, LowerBack: 6, UpperBack: 10,
};
const VOLUME_COLORS = { high: "#2ECC71", medium: "#F5A623", low: "#E74C3C" };
export function getVolumeColor(muscle, totalSets, weeks = 1) {
  // Normalize muscle name to match thresholds
  const key = muscle.replace(/\s+/g, "");
  const threshold = VOLUME_THRESHOLDS[key] || VOLUME_THRESHOLDS[muscle] || 6;
  // Compare total sets against threshold scaled by weeks
  const target = threshold * Math.max(1, weeks);
  if (totalSets >= target) return VOLUME_COLORS.high;
  if (totalSets >= target * 0.6) return VOLUME_COLORS.medium;
  return VOLUME_COLORS.low;
}

// =============================================================================
// CONSTANTS
// =============================================================================
export const CATEGORIES = ["Barbell","Dumbbell","Cable","Machine","Bodyweight","Kettlebell","Band","Cardio","Olympic","Mobility","Other"];

export const MUSCLE_GROUPS = [
  "Chest","Back","Shoulders","Biceps","Triceps","Forearms",
  "Core","Quads","Hamstrings","Glutes","Calves","Full Body","Cardio","Other"
];

// Standard plate sets in kg
const PLATE_SETS_KG = [25, 20, 15, 10, 5, 2.5, 1.25, 0.5];
const PLATE_SETS_LBS = [45, 35, 25, 10, 5, 2.5, 1.25];

// =============================================================================
// HELPERS
// =============================================================================

// Epley 1RM formula
export function epley1RM(weight, reps) {
  if (!weight || !reps || reps <= 0) return 0;
  if (reps === 1) return weight;
  return +(weight * (1 + reps / 30)).toFixed(1);
}

// Plate calculator: given target weight and bar weight, returns plates per side
export function calcPlates(targetKg, barKg, unit) {
  const plates = unit === "lbs" ? PLATE_SETS_LBS : PLATE_SETS_KG;
  let remaining = (targetKg - barKg) / 2;
  const result = [];
  for (const p of plates) {
    if (remaining <= 0) break;
    const count = Math.floor(remaining / p);
    if (count > 0) { result.push({ plate: p, count }); remaining -= count * p; }
  }
  return result;
}

export function uid() { return "wp_" + Date.now().toString(36) + Math.random().toString(36).slice(2,7); }

export function fmtWeight(kg, unit) {
  if(!kg && kg!==0) return "--";
  if(unit==="lbs") return (kg*2.20462).toFixed(1)+" lbs";
  return (+kg)+" kg";
}

export function toKg(val, unit) {
  const n=parseFloat(val)||0;
  return unit==="lbs" ? +(n/2.20462).toFixed(2) : n;
}

export function totalVolume(session, unit) {
  let v=0;
  for(const ex of (session.exercises||[])) for(const s of (ex.sets||[])) if(s.done) v+=(s.weight||0)*(s.reps||0);
  if(unit==="lbs") return (v*2.20462).toFixed(0)+" lbs";
  return v.toFixed(0)+" kg";
}

export function completedSets(session) {
  let n=0;
  for(const ex of (session.exercises||[])) for(const s of (ex.sets||[])) if(s.done) n++;
  return n;
}

export function buildPRMap(sessions) {
  const map={};
  for(const sess of sessions) {
    for(const ex of (sess.exercises||[])) {
      for(const s of (ex.sets||[])) {
        if(!s.done||!s.weight) continue;
        if(!map[ex.exerciseId]||s.weight>map[ex.exerciseId].maxWeight)
          map[ex.exerciseId]={maxWeight:s.weight, date:sess.date};
      }
    }
  }
  return map;
}

export function exerciseVolumeSeries(sessions, exerciseId, n=10) {
  const pts=[];
  for(const sess of [...sessions].sort((a,b)=>a.date<b.date?-1:1)) {
    const ex=(sess.exercises||[]).find(e=>e.exerciseId===exerciseId);
    if(!ex) continue;
    let vol=0;
    for(const s of ex.sets) if(s.done) vol+=(s.weight||0)*(s.reps||0);
    if(vol>0) pts.push({date:sess.date, vol});
  }
  return pts.slice(-n);
}

export function lsGet(key,fb){ try{ const v=localStorage.getItem(key); return v?JSON.parse(v):fb; }catch(_){ return fb; } }
export function lsSet(key,v){ try{ localStorage.setItem(key,JSON.stringify(v)); }catch(_){} }

// Week/day helpers for programmes
export function getActiveProgrammeProgress(prog) {
  if(!prog||!prog.started_at) return {week:0,day:0};
  const start=new Date(prog.started_at+"T00:00:00");
  const today=new Date(TODAY_ISO()+"T00:00:00");
  const diffDays=Math.floor((today-start)/(1000*60*60*24));
  const weeks=prog.weeks||[];
  if(!weeks.length) return {week:0,day:0};
  const totalDays=weeks.reduce((n,w)=>(w.days||[]).length+n,0);
  const clampedDay=Math.min(diffDays,totalDays-1);
  let rem=clampedDay;
  for(let wi=0;wi<weeks.length;wi++){
    const days=(weeks[wi].days||[]).length;
    if(rem<days) return {week:wi,day:rem};
    rem-=days;
  }
  return {week:weeks.length-1,day:0};
}

// =============================================================================
// MUSCLE GROUP VOLUME MAP
// =============================================================================
export function buildMuscleVolumeMap(sessions, weeks, library) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - weeks * 7);
  const cutoffISO = `${cutoff.getFullYear()}-${String(cutoff.getMonth()+1).padStart(2,"0")}-${String(cutoff.getDate()).padStart(2,"0")}`;
  const map = {};
  const libMap = {};
  (library||[]).forEach(ex => { libMap[ex.id] = ex; });
  (sessions||[]).forEach(sess => {
    if (!sess.date || sess.date < cutoffISO) return;
    (sess.exercises||[]).forEach(ex => {
      const libEx = libMap[ex.exerciseId];
      if (!libEx || !libEx.muscles || libEx.muscles.length === 0) return;
      if (libEx.category === "Cardio" || libEx.category === "Mobility") return;
      const primaryMuscle = libEx.muscles[0];
      const doneSets = (ex.sets||[]).filter(s => s.done).length;
      if (doneSets === 0) return;
      map[primaryMuscle] = (map[primaryMuscle] || 0) + doneSets;
    });
  });
  return Object.entries(map)
    .filter(([,v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
}

// =============================================================================
// PRIMITIVE COMPONENTS (shared)
// =============================================================================
export function ComicCard(props){ return _ComicCard(props); }
export function Btn(props){ return _Btn(props); }
export function Field(props){ return _Field(props); }
export function SectionLabel({children}){
  return <p style={{color:T.textSub,fontSize:11,fontFamily:T.fontSans,letterSpacing:"0.05em",margin:"0 0 12px",textTransform:"uppercase",fontWeight:700}}>{children}</p>;
}
export function Spinner({size=14}){
  const [d,setD]=useState(0);
  useEffect(()=>{const t=setInterval(()=>setD(x=>x+15),50);return()=>clearInterval(t);},[]);
  return <span style={{display:"inline-block",transform:`rotate(${d}deg)`}}><ArrowClockwise size={size}/></span>;
}
export function Tag({children,color}){
  return <span style={{display:"inline-block",background:color||T.accent+"22",color:color||T.accent,borderRadius:99,padding:"2px 10px",fontSize:10,fontFamily:T.fontSans,fontWeight:600,marginRight:4,marginBottom:3}}>{children}</span>;
}

// =============================================================================
// VOLUME BARS
// =============================================================================
export function VolumeBars({data}){
  if(!data||data.length<2) return <p style={{color:T.textDim,fontSize:10,fontFamily:T.fontSans,margin:0}}>Not enough data yet.</p>;
  const max=Math.max(...data.map(d=>d.vol));
  return(
    <div style={{display:"flex",alignItems:"flex-end",gap:3,height:52}}>
      {data.map((d,i)=>(
        <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
          <div style={{width:"100%",height:max>0?Math.max(4,(d.vol/max)*46):4,background:i===data.length-1?T.accent:T.textDim,border:`1.5px solid ${T.ink}`}}/>
          <span style={{fontSize:7,color:T.textDim,fontFamily:T.fontSans}}>{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// MUSCLE GROUP VOLUME CHART
// =============================================================================
export function MuscleVolumeChart({sessions, library, embedded}) {
  const [weeks, setWeeks] = useState(4);
  const data = buildMuscleVolumeMap(sessions, weeks, library);
  const max  = data.length > 0 ? data[0][1] : 0;
  // V28 FIX 3: Use shared VOLUME_THRESHOLDS + getVolumeColor
  const weekToggle = (
    <div style={{display:"flex",border:`1px solid ${T.cardBorder}`,borderRadius:8,overflow:"hidden"}}>
      {[4,8].map((w,i)=>(
        <button key={w} onClick={()=>setWeeks(w)} style={{
          padding:"4px 10px",background:weeks===w?T.accent:"transparent",
          border:"none",borderRight:i===0?`1px solid ${T.cardBorder}`:"none",
          color:weeks===w?T.ink:T.inkMuted,cursor:"pointer",
          fontSize:8,fontFamily:T.fontSans,fontWeight:700,letterSpacing:"0.5px",
        }}>{w}W</button>
      ))}
    </div>
  );
  if (embedded) return (
    <div style={{padding:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <SectionLabel>Muscle group volume</SectionLabel>
        {weekToggle}
      </div>
      {data.length === 0 ? (
        <p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,margin:0}}>No completed sets in the last {weeks} weeks.</p>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {data.map(([muscle, sets]) => {
            const pct = Math.min(1, sets / Math.max(max, 1));
            const barColor = getVolumeColor(muscle, sets, weeks);
            return (
              <div key={muscle}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                  <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.inkDark,letterSpacing:"0.3px"}}>{muscle}</span>
                  <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:"bold",color:barColor}}>{sets} sets</span>
                </div>
                <div style={{height:8,background:T.surfaceLight,borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct*100}%`,borderRadius:4,background:barColor,transition:"width 0.3s"}}/>
                </div>
              </div>
            );
          })}
          <p style={{color:T.inkMuted,fontSize:8,fontFamily:T.fontSans,fontWeight:700,margin:"6px 0 0"}}>Green = meets minimum volume · Yellow = 60%+ · Red = below threshold</p>
        </div>
      )}
    </div>
  );
  return (
    <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:16,overflow:"hidden"}}>
      <div style={{background:T.tileBg,borderRadius:"18px 18px 0 0",padding:"14px 18px",display:"flex",alignItems:"center",gap:10,borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        <span style={{fontSize:20}}>💪</span>
        <div>
          <div style={{color:T.tileAccent,fontWeight:700,fontSize:15,fontFamily:T.fontDisplay}}>Muscle group volume</div>
          <div style={{color:T.inkFaint,fontSize:12}}>Sets per muscle · last 4 or 8 weeks</div>
        </div>
        <div style={{marginLeft:"auto"}}>{weekToggle}</div>
      </div>
      <div style={{padding:16}}>
        {data.length === 0 ? (
          <p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,margin:0}}>No completed sets in the last {weeks} weeks.</p>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            {data.map(([muscle, sets]) => {
              const pct = Math.min(1, sets / Math.max(max, 1));
              const barColor = getVolumeColor(muscle, sets, weeks);
              return (
                <div key={muscle}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                    <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.inkDark,letterSpacing:"0.3px"}}>{muscle}</span>
                    <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:"bold",color:barColor}}>{sets} sets</span>
                  </div>
                  <div style={{height:8,background:T.surfaceLight,borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct*100}%`,borderRadius:4,background:barColor,transition:"width 0.3s"}}/>
                  </div>
                </div>
              );
            })}
            <p style={{color:T.inkMuted,fontSize:8,fontFamily:T.fontSans,fontWeight:700,margin:"6px 0 0"}}>Green = meets minimum volume · Yellow = 60%+ · Red = below threshold</p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MUSCLE RECOVERY ENGINE
// =============================================================================
export function calcRecoveryMap(sessions, library) {
  if (!sessions || !sessions.length || !library) return {};
  const libMap = {};
  (library||[]).forEach(e => { libMap[e.id] = e; });
  const todayMs = new Date(TODAY_ISO()+"T00:00:00").getTime();
  // For each muscle, find most recent session date + set count in that session
  const lastData = {}; // { muscle: { date, sets } }
  for (const sess of [...sessions].sort((a,b)=>a.date<b.date?-1:1)) {
    const sessMs = new Date(sess.date+"T00:00:00").getTime();
    const muscleSets = {};
    for (const ex of (sess.exercises||[])) {
      const libEx = libMap[ex.exerciseId];
      if (!libEx || libEx.category==="Cardio" || libEx.category==="Mobility") continue;
      const primary = (libEx.muscles||[])[0];
      if (!primary) continue;
      const done = (ex.sets||[]).filter(s=>s.done).length;
      muscleSets[primary] = (muscleSets[primary]||0) + done;
    }
    for (const [muscle, sets] of Object.entries(muscleSets)) {
      lastData[muscle] = { dateMs: sessMs, sets };
    }
  }
  const result = {};
  const ALL_MUSCLES = ["Chest","Back","Shoulders","Biceps","Triceps","Forearms","Core","Quads","Hamstrings","Glutes","Calves"];
  for (const muscle of ALL_MUSCLES) {
    if (!lastData[muscle]) {
      result[muscle] = { pct: null, status: "untrained", daysSince: null, sets: 0 };
      continue;
    }
    const { dateMs, sets } = lastData[muscle];
    const daysSince = Math.floor((todayMs - dateMs) / 86_400_000);
    const recovDays = sets <= 3 ? 1 : sets <= 6 ? 2 : 3;
    const pct = Math.min(100, Math.round((daysSince / recovDays) * 100));
    const status = pct >= 80 ? "ready" : pct >= 50 ? "partial" : "fatigued";
    result[muscle] = { pct, status, daysSince, sets };
  }
  return result;
}

export function RecoveryCard({ sessions, library, embedded }) {
  const [open, setOpen] = useState(false);
  const recovMap = calcRecoveryMap(sessions, library);
  const DOT = { ready:"🟢", partial:"🟡", fatigued:"🔴", untrained:"⚫" };
  const LABEL = { ready:"Ready", partial:"Partial", fatigued:"Fatigued", untrained:"—" };
  const ALL_MUSCLES = ["Chest","Back","Shoulders","Biceps","Triceps","Forearms","Core","Quads","Hamstrings","Glutes","Calves"];
  const header = (
    <button onClick={()=>setOpen(o=>!o)}
      style={{width:"100%",background:"none",border:"none",cursor:"pointer",padding:0,display:"block"}}>
      <div style={{background:T.tileBg,borderRadius:"18px 18px 0 0",padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>⚡</span>
          <div>
            <div style={{color:T.tileAccent,fontWeight:700,fontSize:15,fontFamily:T.fontDisplay,textAlign:"left"}}>Muscle recovery</div>
            <div style={{color:T.inkFaint,fontSize:12,textAlign:"left"}}>Readiness by muscle group</div>
          </div>
        </div>
        <span style={{color:T.inkMuted,fontSize:13,fontFamily:T.fontSans}}>{open?"▲ Collapse":"▼ Expand"}</span>
      </div>
    </button>
  );
  const body = open && (
    <div style={{padding:"12px 16px",display:"flex",flexDirection:"column",gap:5}}>
      {ALL_MUSCLES.map(m => {
        const d = recovMap[m] || { pct:null, status:"untrained", daysSince:null };
        return (
          <div key={m} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
            padding:"4px 0",borderBottom:`1px solid ${T.cardBorder}`}}>
            <span style={{fontSize:12,fontFamily:T.fontSans,fontWeight:700,color:T.inkDark,minWidth:90}}>{m}</span>
            <span style={{fontSize:13}}>{DOT[d.status]}</span>
            <span style={{fontSize:11,fontFamily:T.fontSans,color:T.inkMuted,minWidth:60,textAlign:"right"}}>
              {d.status==="untrained" ? "Never" : `${d.pct}%`}
            </span>
            <span style={{fontSize:10,fontFamily:T.fontSans,color:T.inkFaint,minWidth:50,textAlign:"right"}}>
              {d.daysSince===null ? "" : d.daysSince===0 ? "Today" : `${d.daysSince}d ago`}
            </span>
          </div>
        );
      })}
    </div>
  );
  if (embedded) return <div>{header}{body}</div>;
  return (
    <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:14,overflow:"hidden"}}>
      {header}{body}
    </div>
  );
}

// =============================================================================
// WORKOUT STREAK
// =============================================================================
export function calcWorkoutStreak(sessions) {
  if(!sessions||!sessions.length) return {current:0,best:0};
  const dates=[...new Set(sessions.map(s=>s.date))].sort((a,b)=>b<a?-1:1);
  let current=0,best=0,run=0;
  const _lf=(dt)=>`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
  const today=TODAY_ISO();
  const yest=new Date(); yest.setDate(yest.getDate()-1);
  const yestISO=_lf(yest);
  let prev=null;
  for(const d of dates){
    if(prev===null){
      if(d!==today&&d!==yestISO) break;
      run=1;
    } else {
      const exp=new Date(prev+"T00:00:00");
      exp.setDate(exp.getDate()-1);
      if(d!==_lf(exp)) break;
      run++;
    }
    prev=d;
  }
  current=run;
  run=0; let bprev=null;
  for(const d of [...dates].reverse()){
    if(bprev===null){run=1;}
    else{
      const exp=new Date(bprev+"T00:00:00");
      exp.setDate(exp.getDate()+1);
      run=d===_lf(exp)?run+1:1;
    }
    if(run>best) best=run;
    bprev=d;
  }
  return {current,best};
}

// =============================================================================
// REST TIMER COMPONENT
// =============================================================================
export function RestTimer(){
  const [secs,setSecs]=useState(90);
  const [active,setActive]=useState(false);
  const [remaining,setRemaining]=useState(90);
  const endTimeRef=useRef(null);
  useEffect(()=>{
    if(!active) return;
    endTimeRef.current=Date.now()+(remaining*1000);
    const t=setInterval(()=>{
      const r=Math.max(0,Math.round((endTimeRef.current-Date.now())/1000));
      setRemaining(r);
      if(r<=0){setActive(false);clearInterval(t);}
    },250);
    return()=>clearInterval(t);
  },[active]); // eslint-disable-line react-hooks/exhaustive-deps
  function start(){setRemaining(secs);setActive(true);}
  function stop(){setActive(false);setRemaining(secs);}
  return(
    <div style={{background:T.card,border:`1px solid ${T.cardBorder||T.ink}`,borderRadius:T.radiusSm,padding:"12px 14px",display:"flex",alignItems:"center",gap:12,marginBottom:10,flexWrap:"wrap"}}>
      <Clock size={16} color={T.textDim}/>
      <span style={{fontSize:12,fontFamily:T.fontSans,fontWeight:700,color:T.textSub}}>Rest</span>
      <select value={secs} onChange={e=>{setSecs(+e.target.value);setRemaining(+e.target.value);}} disabled={active}
        style={{background:T.surfaceLight||T.surface,border:`1px solid ${T.cardBorder||T.ink}`,borderRadius:T.radiusSm,color:T.text,fontFamily:T.fontSans,fontWeight:600,fontSize:13,padding:"6px 10px"}}>
        {[30,45,60,90,120,180,240,300].map(s=><option key={s} value={s}>{fmtDuration(s)}</option>)}
      </select>
      {active&&<span style={{fontSize:22,fontFamily:T.fontDisplay,fontWeight:800,color:remaining<10?T.red:T.tileAccent||T.accent,minWidth:44}}>{remaining}s</span>}
      {active
        ?<button onClick={stop} style={{background:"none",border:`1.5px solid ${T.red||"#e74c3c"}`,color:T.red||"#e74c3c",borderRadius:T.radiusSm,padding:"6px 14px",cursor:"pointer",fontSize:12,fontFamily:T.fontSans,fontWeight:700}}>Stop</button>
        :<button onClick={start} style={{background:T.primary||T.accent,border:"none",color:"#fff",borderRadius:T.radiusSm,padding:"6px 14px",cursor:"pointer",fontSize:12,fontFamily:T.fontSans,fontWeight:700}}>Start</button>}
    </div>
  );
}

// =============================================================================
// EXERCISE PICKER MODAL
// =============================================================================
export function ExercisePicker({library,onSelect,onClose}){
  const [search,setSearch]=useState("");
  const [filterMode,setFilterMode]=useState("category"); // "category" | "muscle"
  const [catFilter,setCatFilter]=useState("All");
  const [muscleFilter,setMuscleFilter]=useState("All");
  const filtered=library.filter(ex=>{
    const ms=ex.name.toLowerCase().includes(search.toLowerCase());
    const mc=catFilter==="All"||ex.category===catFilter;
    const mm=muscleFilter==="All"||(ex.muscles||[]).includes(muscleFilter);
    return ms&&mc&&mm;
  });
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:200}}>
      <div style={{background:T.bg,border:T.border,boxShadow:T.shadow,borderRadius:`${T.radius} ${T.radius} 0 0`,width:"100%",maxWidth:480,maxHeight:"82vh",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"14px 16px 10px",borderBottom:`2px solid ${T.ink}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span style={{fontSize:14,fontFamily:T.fontSans,fontWeight:900,letterSpacing:"1px",textTransform:"uppercase"}}>Add Exercise</span>
            <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:T.ink}}><X size={18}/></button>
          </div>
          <Field placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)} icon={<MagnifyingGlass size={13}/>}/>
          {/* Filter mode toggle */}
          <div style={{display:"flex",gap:6,marginTop:8,marginBottom:6}}>
            {[{id:"category",label:"By Equipment"},{id:"muscle",label:"By Muscle"}].map(({id,label})=>(
              <button key={id} onClick={()=>{setFilterMode(id);setCatFilter("All");setMuscleFilter("All");}}
                style={{flex:1,padding:"5px 0",background:filterMode===id?T.ink:T.surface,
                  color:filterMode===id?T.card:T.textSub,border:`2px solid ${T.ink}`,fontSize:9,
                  fontFamily:T.fontSans,fontWeight:900,cursor:"pointer",letterSpacing:"0.5px",textTransform:"uppercase"}}>{label}</button>
            ))}
          </div>
          {filterMode==="category"&&(
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {["All",...CATEGORIES].map(f=>(
                <button key={f} onClick={()=>setCatFilter(f)} style={{background:catFilter===f?T.ink:T.surface,color:catFilter===f?T.card:T.textSub,border:`2px solid ${T.ink}`,padding:"3px 8px",fontSize:9,fontFamily:T.fontSans,fontWeight:900,cursor:"pointer",letterSpacing:"0.5px",textTransform:"uppercase"}}>{f}</button>
              ))}
            </div>
          )}
          {filterMode==="muscle"&&(
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {["All",...MUSCLE_GROUPS].map(m=>(
                <button key={m} onClick={()=>setMuscleFilter(m)} style={{background:muscleFilter===m?T.ink:T.surface,color:muscleFilter===m?T.card:T.textSub,border:`2px solid ${T.ink}`,padding:"3px 8px",fontSize:9,fontFamily:T.fontSans,fontWeight:900,cursor:"pointer",letterSpacing:"0.5px",textTransform:"uppercase"}}>{m}</button>
              ))}
            </div>
          )}
        </div>
        <div style={{overflowY:"auto",flex:1}}>
          {filtered.map(ex=>{
            const isNativeCtx = isNative;
            return(
            <div key={ex.id} style={{display:"flex",alignItems:"center",borderBottom:`1px solid ${T.surface}`}}>
              <button onClick={()=>onSelect(ex)} style={{flex:1,textAlign:"left",background:"none",border:"none",padding:"10px 16px",cursor:"pointer"}}>
                <p style={{margin:0,fontSize:13,fontFamily:T.fontSans,fontWeight:900,color:T.text,textTransform:"uppercase"}}>{ex.name}</p>
                <p style={{margin:"2px 0 0",fontSize:9,fontFamily:T.fontSans,color:T.textDim}}>{ex.category} &bull; {(ex.muscles||[]).join(", ")}</p>
                {ex.notes&&<p style={{margin:"2px 0 0",fontSize:9,fontFamily:T.fontSans,color:T.textDim,fontStyle:"italic"}}>{ex.notes}</p>}
              </button>
              {ex.videoUrl&&(
                <button onClick={e=>{e.stopPropagation();window.open(ex.videoUrl,isNativeCtx?"_system":"_blank");}}
                  style={{flexShrink:0,marginRight:10,background:"none",border:`1px solid ${T.textDim}`,
                    padding:"2px 6px",cursor:"pointer",color:T.textDim,fontSize:8,fontFamily:T.fontSans,fontWeight:900}}>
                  DEMO
                </button>
              )}
            </div>
            );
          })}
          {filtered.length===0&&<p style={{padding:"20px 16px",textAlign:"center",fontSize:11,fontFamily:T.fontSans,color:T.textDim}}>No matches. Add a custom exercise in the Library tab.</p>}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// EXERCISE BLOCK (used in active workout)
// =============================================================================
export function ExerciseBlock({ex,exIdx,unit,onAddSet,onRemoveSet,onUpdateSet,onToggleDone,onRemoveExercise,previousSets,allDone,onSubstitute,canSuperset,isSuperset,onToggleSuperset,libEx,onDone}){
  const [collapsed,setCollapsed]=useState(false);
  const [showHowTo,setShowHowTo]=useState(false);
  const [showRpeHint,setShowRpeHint]=useState(false);
  // Compute summary stats for collapsed view
  const doneSets=ex.sets.filter(s=>s.done||(s.weight&&parseFloat(s.weight)>0&&s.reps&&parseInt(s.reps)>0));
  const totalVol=doneSets.reduce((a,s)=>(a+(parseFloat(s.weight)||0)*(parseInt(s.reps)||0)),0);
  const chipStyle={flexShrink:0,background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.18)",
    padding:"3px 7px",cursor:"pointer",color:"rgba(255,255,255,0.85)",fontSize:8,fontFamily:T.fontSans,fontWeight:700,borderRadius:12};
  return(
    <div style={{background:T.card,borderRadius:18,marginBottom:0,overflow:"hidden",border:`1px solid ${T.cardBorder||T.ink}`,
      ...(allDone?{borderLeft:`4px solid ${T.tileAccent}`}:{})}}>
      <div style={{display:"flex",alignItems:"center",padding:"10px 14px",background:T.tileBg,transition:"background 0.2s",gap:6}}>
        <button onClick={()=>setCollapsed(c=>!c)} style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0}}>
          {collapsed?<CaretRight size={14} color="rgba(255,255,255,0.4)"/>:<CaretDown size={14} color="rgba(255,255,255,0.4)"/>}
          <div style={{flex:1,minWidth:0,textAlign:"left"}}>
            <span style={{fontSize:16,fontFamily:T.fontDisplay,fontWeight:800,color:"#fff",lineHeight:1.2,display:"block",textAlign:"left"}}>{ex.name}</span>
            {ex.pbBest&&ex.pbBest.weight>0&&(
              <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:600,color:"rgba(255,255,255,0.40)",display:"block",textAlign:"left",marginTop:1}}>
                Previous best: {fmtWeight(ex.pbBest.weight,unit)} × {ex.pbBest.reps} reps
              </span>
            )}
            {collapsed&&doneSets.length>0&&(
              <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:600,color:T.tileAccent,display:"block",textAlign:"left",marginTop:1}}>
                {doneSets.length}/{ex.sets.length} sets{totalVol>0?` · ${Math.round(totalVol)} ${unit==="lbs"?"lbs":"kg"}`:""}
              </span>
            )}
            {!collapsed&&ex.sets.length>0&&(
              <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:600,color:"rgba(255,255,255,0.45)",display:"block",textAlign:"left",marginTop:1}}>
                {ex.sets.length} set{ex.sets.length!==1?"s":""}
              </span>
            )}
          </div>
        </button>
        <div style={{display:"flex",gap:4,alignItems:"center",flexShrink:0}}>
          {allDone&&<Check size={14} color={T.tileAccent} style={{flexShrink:0}}/>}
          {libEx?.instructions&&(
            <button onClick={e=>{e.stopPropagation();setShowHowTo(h=>!h);}}
              style={{...chipStyle,background:showHowTo?(T.primary||T.accent):"rgba(255,255,255,0.12)",
                color:showHowTo?"#fff":"rgba(255,255,255,0.85)",border:showHowTo?`1px solid ${T.primary||T.accent}`:"1px solid rgba(255,255,255,0.18)"}}>
              How to
            </button>
          )}
          {libEx?.videoUrl&&(()=>{
            const isNativeCtx = isNative;
            return(
              <button onClick={e=>{e.stopPropagation();window.open(libEx.videoUrl,isNativeCtx?"_system":"_blank");}}
                style={chipStyle}>
                Demo
              </button>
            );
          })()}
          {canSuperset&&(
            <button onClick={onToggleSuperset} title="Superset with next exercise"
              style={{...chipStyle,background:isSuperset?T.tileAccent:"rgba(255,255,255,0.12)",
                color:isSuperset?T.tileBg:"rgba(255,255,255,0.85)",border:isSuperset?`1px solid ${T.tileAccent}`:"1px solid rgba(255,255,255,0.18)"}}>
              SS
            </button>
          )}
          {onSubstitute&&(
            <button onClick={onSubstitute} title="Substitute exercise" style={chipStyle}>Sub</button>
          )}
          <button onClick={onRemoveExercise} style={{...chipStyle,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.10)"}}><Trash size={12}/></button>
        </div>
      </div>
      {showHowTo&&libEx?.instructions&&(
        <div style={{background:T.surfaceLight||T.surface,padding:"12px 14px",margin:"0 14px 6px",borderRadius:T.radiusSm,borderLeft:`3px solid ${T.primary||T.accent}`}}>
          <p style={{margin:0,fontSize:13,fontFamily:T.fontSans,color:T.text,lineHeight:"1.6"}}>{libEx.instructions}</p>
        </div>
      )}
      {!collapsed&&(
        <div style={{padding:"10px 14px 14px"}}>
          {previousSets&&previousSets.length>0&&(()=>{
            const donePrev=previousSets.filter(s=>s.done&&parseFloat(s.weight)>0);
            const maxW=donePrev.length>0?Math.max(...donePrev.map(s=>parseFloat(s.weight))):0;
            const maxSet=donePrev.find(s=>parseFloat(s.weight)===maxW);
            const avgRpe=donePrev.length>0?(donePrev.reduce((a,s)=>a+(parseFloat(s.rpe)||7),0)/donePrev.length):7;
            let overloadTip=null;
            if(maxW>0){
              if(avgRpe<=7){
                const sug=maxW<20?(maxW+1).toFixed(1):(maxW+2.5).toFixed(1);
                overloadTip=`Last: ${fmtWeight(maxW,unit)}×${maxSet?.reps} (RPE ${avgRpe.toFixed(0)}). Try ${fmtWeight(parseFloat(sug),unit)} today.`;
              } else if(avgRpe<=8.5){
                overloadTip=`Last: ${fmtWeight(maxW,unit)}×${maxSet?.reps} (RPE ${avgRpe.toFixed(0)}). Match weight, push for more reps.`;
              } else {
                overloadTip=`Last: ${fmtWeight(maxW,unit)}×${maxSet?.reps} (RPE ${avgRpe.toFixed(0)}). Hold this weight and nail the reps.`;
              }
            }
            return(<>
              <div style={{marginBottom:overloadTip?6:10,padding:"8px 10px",background:T.surfaceLight||T.surface,borderRadius:T.radiusSm,border:`1px solid ${T.cardBorder||T.ink}`}}>
                <p style={{margin:"0 0 4px",fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.textDim}}>Previous</p>
                {previousSets.slice(0,3).map((s,i)=>(
                  <span key={i} style={{fontSize:11,fontFamily:T.fontSans,fontWeight:600,color:T.tileAccent||T.accent,marginRight:12}}>
                    {fmtWeight(s.weight,unit)} × {s.reps}
                  </span>
                ))}
              </div>
              {overloadTip&&(
                <div style={{background:(T.primary||T.accent)+"12",borderRadius:T.radiusSm,padding:"8px 10px",marginBottom:10}}>
                  <p style={{margin:"0 0 3px",fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.primary||T.accent}}>Progression</p>
                  <p style={{margin:0,fontSize:12,fontFamily:T.fontSans,color:T.text,lineHeight:"1.5"}}>{overloadTip}</p>
                </div>
              )}
            </>);
          })()}
          <div style={{marginBottom:showRpeHint?4:6}}>
            <div style={{display:"grid",gridTemplateColumns:"40px 1.2fr 0.8fr 50px 20px",gap:5,alignItems:"center"}}>
              <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.textDim}}>Type</span>
              <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.textDim}}>{unit==="lbs"?"lbs":"kg"}</span>
              <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.textDim}}>Reps</span>
              <button onClick={()=>setShowRpeHint(h=>!h)} style={{background:"none",border:"none",cursor:"pointer",padding:"8px 4px",margin:"-8px -4px",minHeight:44,display:"flex",alignItems:"center",gap:3}}>
                <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:showRpeHint?(T.primary||T.accent):T.textDim}}>RPE</span>
                <Info size={12} weight={showRpeHint?"fill":"regular"} color={showRpeHint?(T.primary||T.accent):T.textDim}/>
              </button>
              <span/>
            </div>
            {showRpeHint&&(
              <div style={{background:T.surfaceLight||T.surface,border:`1px solid ${T.cardBorder||T.ink}`,borderRadius:T.radiusXs||8,padding:"8px 12px",marginTop:4}}>
                <p style={{margin:0,fontSize:11,fontFamily:T.fontSans,color:T.textSub||T.inkMuted,lineHeight:"1.5"}}>
                  <strong>Rate of Perceived Exertion (1–10).</strong> How hard did the set feel? 6 = moderate, 8 = hard, 10 = maximum effort. Optional — leave blank if unsure.
                </p>
              </div>
            )}
          </div>
          {ex.sets.map((s,si)=>{
            const setType = s.setType||"normal";
            const SET_TYPES = ["normal","warmup","drop","failure"];
            const SET_LABELS = {normal:"Work",warmup:"Warm",drop:"Drop",failure:"Fail"};
            const SET_BG = {normal:T.surfaceLight||T.surface,warmup:"#fef3cd",drop:"#e2d6f3",failure:"#fce4e4"};
            const SET_TEXT = {normal:T.textSub||T.text,warmup:"#856404",drop:"#6f42c1",failure:"#c0392b"};
            const nextType = SET_TYPES[(SET_TYPES.indexOf(setType)+1)%SET_TYPES.length];
            const inputBase={background:T.surfaceLight||T.surface,border:`1px solid ${T.cardBorder||T.ink}`,borderRadius:T.radiusSm,color:T.text,fontFamily:T.fontSans,fontWeight:600,fontSize:16,padding:"8px 10px",outline:"none",width:"100%",boxSizing:"border-box"};
            // Real-time PR detection — shown as gold Type button tint
            const sw=parseFloat(s.weight)||0, sr=parseInt(s.reps)||0;
            const prevBestW=previousSets&&previousSets.length>0?Math.max(0,...previousSets.map(p=>parseFloat(p.weight)||0)):0;
            const prevBest1RM=previousSets&&previousSets.length>0?Math.max(0,...previousSets.map(p=>epley1RM(parseFloat(p.weight)||0,parseInt(p.reps)||0))):0;
            const cur1RM=sw>0&&sr>0?epley1RM(sw,sr):0;
            const isPR=sw>0&&((sw>prevBestW&&prevBestW>0)||(cur1RM>prevBest1RM&&prevBest1RM>0));
            return(
            <div key={si} style={{display:"grid",gridTemplateColumns:"40px 1.2fr 0.8fr 50px 20px",gap:5,alignItems:"center",marginBottom:5,
              ...(s.done?{background:T.accentLight,borderRadius:T.radiusSm,padding:"2px 0"}:{})}}>
              <button onClick={()=>onUpdateSet(si,"setType",nextType)} title={isPR?"PR alert! Exceeds previous best":`Set type: ${setType} — tap to cycle`}
                style={{height:28,background:isPR?(T.gold||"#fbbf24")+"33":SET_BG[setType],border:isPR?`1.5px solid ${T.gold||"#fbbf24"}`:"none",borderRadius:8,cursor:"pointer",
                  fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:isPR?T.gold||"#fbbf24":SET_TEXT[setType],
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                {SET_LABELS[setType]}
              </button>
              <input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" value={s.weight} onChange={e=>onUpdateSet(si,"weight",e.target.value)} placeholder="0"
                style={inputBase}/>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={s.reps} onChange={e=>onUpdateSet(si,"reps",e.target.value)} placeholder="0"
                style={inputBase}/>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={s.rpe} onChange={e=>onUpdateSet(si,"rpe",e.target.value)} placeholder="--"
                style={{...inputBase,fontSize:14}}/>
              <button onClick={()=>onRemoveSet(si)} style={{background:"none",border:"none",cursor:"pointer",color:T.textDim,display:"flex",justifyContent:"center"}}><X size={14}/></button>
            </div>
            );
          })}
          <button onClick={onAddSet} style={{width:"100%",background:"none",border:`1.5px dashed ${T.cardBorder||T.ink}`,color:T.textSub,padding:"10px 0",cursor:"pointer",fontSize:12,fontFamily:T.fontSans,fontWeight:700,borderRadius:T.radiusSm,marginTop:10}}>
            + Add set
          </button>
          <button onClick={()=>{if(onDone)onDone();else setCollapsed(true);}} style={{width:"100%",display:"block",background:T.primary||T.accent,border:"none",color:"#fff",padding:"11px 0",cursor:"pointer",fontSize:12,fontFamily:T.fontSans,fontWeight:700,borderRadius:T.radiusSm,marginTop:10}}>
            Done
          </button>
          {(()=>{
            const doneSets=ex.sets.filter(s=>s.done&&s.weight&&s.reps);
            const best1RM=doneSets.reduce((b,s)=>{
              const e=epley1RM(parseFloat(s.weight),parseInt(s.reps));
              return e>b?e:b;
            },0);
            const allDone=ex.sets.length>0&&ex.sets.every(s=>s.done);
            const prevBestW=previousSets&&previousSets.length>0?Math.max(...previousSets.map(p=>parseFloat(p.weight)||0)):0;
            const curBestW=doneSets.length>0?Math.max(...doneSets.map(s=>parseFloat(s.weight)||0)):0;
            const showOverload=allDone&&prevBestW>0&&curBestW>=prevBestW;
            if(!best1RM&&!showOverload) return null;
            return(
              <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:6}}>
                {best1RM>0&&(
                  <div style={{padding:"8px 10px",background:T.surfaceLight||T.surface,borderRadius:T.radiusSm,border:`1px solid ${T.cardBorder||T.ink}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.textDim}}>Est. 1RM</span>
                    <span style={{fontSize:14,fontFamily:T.fontDisplay,fontWeight:800,color:T.gold}}>{best1RM} kg</span>
                  </div>
                )}
                {showOverload&&(
                  <div style={{padding:"8px 10px",background:T.tileAccent||T.accent,borderRadius:T.radiusSm,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:"#fff"}}>Next session</span>
                    <span style={{fontSize:13,fontFamily:T.fontDisplay,fontWeight:800,color:"#fff"}}>Try {(curBestW+2.5).toFixed(1)} kg</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PLATE CALCULATOR MODAL
// =============================================================================
export function PlateCalculator({ unit, onClose }) {
  const defaultBar = unit === "lbs" ? 45 : 20;
  const [target, setTarget] = useState("");
  const [barWeight, setBarWeight] = useState(String(defaultBar));

  const targetNum = parseFloat(target) || 0;
  const barNum = parseFloat(barWeight) || defaultBar;
  const displayUnit = unit;
  // Convert to kg internally for calc, then display in chosen unit
  const targetKg = unit === "lbs" ? targetNum / 2.20462 : targetNum;
  const barKg = unit === "lbs" ? barNum / 2.20462 : barNum;
  const plates = targetKg > barKg ? calcPlates(targetKg, barKg, "kg") : [];
  const achievableKg = barKg + plates.reduce((s,p)=>s+p.plate*p.count*2, 0);
  const achievable = unit === "lbs" ? (achievableKg * 2.20462).toFixed(1) : achievableKg.toFixed(1);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}>
      <div style={{background:T.bg,border:T.border,boxShadow:T.shadow,borderRadius:T.radius,width:"90%",maxWidth:380,padding:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <span style={{fontSize:16,fontFamily:T.fontSans,fontWeight:900,textTransform:"uppercase",letterSpacing:"1px",color:T.text}}>Plate Calculator</span>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:T.ink}}><X size={18}/></button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
          <div>
            <label style={{color:T.textSub,fontSize:10,fontFamily:T.fontSans,letterSpacing:"1px",display:"block",marginBottom:4,textTransform:"uppercase",fontWeight:900}}>Target ({displayUnit})</label>
            <input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" value={target} onChange={e=>setTarget(e.target.value)} placeholder={unit==="lbs"?"135":"100"}
              style={{width:"100%",background:T.surface,border:T.border,borderRadius:T.radiusXs,color:T.text,fontFamily:T.fontSans,fontWeight:"bold",fontSize:16,padding:"10px 10px",outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{color:T.textSub,fontSize:10,fontFamily:T.fontSans,letterSpacing:"1px",display:"block",marginBottom:4,textTransform:"uppercase",fontWeight:900}}>Bar ({displayUnit})</label>
            <input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" value={barWeight} onChange={e=>setBarWeight(e.target.value)}
              style={{width:"100%",background:T.surface,border:T.border,borderRadius:T.radiusXs,color:T.text,fontFamily:T.fontSans,fontWeight:"bold",fontSize:16,padding:"10px 10px",outline:"none",boxSizing:"border-box"}}/>
          </div>
        </div>
        {targetNum > 0 && targetNum > barNum && (
          <div>
            <p style={{margin:"0 0 8px",fontSize:9,fontFamily:T.fontSans,fontWeight:900,color:T.textDim,textTransform:"uppercase",letterSpacing:"0.5px"}}>Plates per side</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:12}}>
              {plates.length > 0 ? plates.map((p,i)=>(
                <div key={i} style={{background:T.card,border:T.border,boxShadow:T.shadowXs,borderRadius:T.radiusSm,padding:"8px 14px",textAlign:"center"}}>
                  <p style={{margin:0,fontSize:20,fontFamily:T.fontSans,fontWeight:900,color:T.accent}}>x{p.count}</p>
                  <p style={{margin:"2px 0 0",fontSize:10,fontFamily:T.fontSans,color:T.textSub,fontWeight:"bold"}}>{unit==="lbs"?(p.plate*2.20462).toFixed(1):p.plate}{displayUnit}</p>
                </div>
              )) : <p style={{color:T.textDim,fontSize:11,fontFamily:T.fontSans}}>No standard plates needed</p>}
            </div>
            <div style={{background:T.cardHi,border:T.border,borderRadius:T.radiusSm,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:900,color:T.textSub,textTransform:"uppercase"}}>Achievable</span>
              <span style={{fontSize:16,fontFamily:T.fontSans,fontWeight:900,color:T.accent}}>{achievable} {displayUnit}</span>
            </div>
          </div>
        )}
        {targetNum > 0 && targetNum <= barNum && (
          <p style={{color:T.textDim,fontSize:11,fontFamily:T.fontSans,fontWeight:"bold"}}>Target must exceed bar weight ({barNum}{displayUnit})</p>
        )}
      </div>
    </div>
  );
}
